import { useRef, useEffect, useCallback } from "react";
import { useFlushOnPageHide } from "@/hooks/use-flush-on-page-hide";
import { useMutation } from "@apollo/client";
import { UPDATE_CLIP_DATA } from "@/queries/queries";
import { serializeCoverImage, type ClipCoverImage } from "@/lib/cover/coverImage";

interface Options {
  clipId: string | null;
  coverImage: ClipCoverImage | null;
  /** The clip the CURRENT `coverImage` belongs to — same stale-window tag as
   *  useAutoSaveClipData; see the rationale there. */
  loadedClipId: string | null;
  enabled: boolean;
  delay?: number;
  onSaved?: (clipId: string, data: { coverImage: ClipCoverImage | null }) => void;
}

type PendingWrite = {
  clipId: string;
  coverImage: ClipCoverImage | null;
  serialized: string;
};

/** Debounced persistence of the per-clip cover image (clip.data.coverImage).
 *  Clone of useAutoSaveTransitions/useAutoSaveCropKeyframes, with ONE
 *  difference: `null` is a real value here (removing the cover), not "no
 *  overrides yet" — it IS written, as `{ coverImage: null }`, since the
 *  backend shallow-merges clip.data and omitting the key would not clear it.
 *  Pending writes are flushed (never dropped) on clip switch and unmount. */
export function useAutoSaveCoverImage({
  clipId,
  coverImage,
  loadedClipId,
  enabled,
  delay = 500,
  onSaved,
}: Options) {
  const [updateClipData, { loading: saving }] = useMutation(UPDATE_CLIP_DATA);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<PendingWrite | null>(null);
  const lastSavedRef = useRef<string>("");
  const awaitingLoadRef = useRef<boolean>(true);
  const currentClipIdRef = useRef<string | null>(null);
  const onSavedRef = useRef(onSaved);

  useEffect(() => {
    onSavedRef.current = onSaved;
  }, [onSaved]);

  const performSave = useCallback(
    async (write: PendingWrite) => {
      try {
        const payload = { coverImage: write.coverImage };
        await updateClipData({
          variables: { id: write.clipId, data: payload },
        });
        lastSavedRef.current = write.serialized;
        onSavedRef.current?.(write.clipId, payload);
      } catch (error) {
        console.error("Auto-save cover image failed:", error);
      }
    },
    [updateClipData],
  );

  /** Fire the pending write NOW (fire-and-forget). Never drops it. */
  const firePending = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending) void performSave(pending);
  }, [performSave]);
  // Tab close never unmounts: fire the pending write as a keepalive request.
  useFlushOnPageHide(firePending);
  const firePendingRef = useRef(firePending);
  useEffect(() => {
    firePendingRef.current = firePending;
  }, [firePending]);

  // Clip switch: flush the old clip's pending write, await the new load.
  useEffect(() => {
    if (clipId !== currentClipIdRef.current) {
      firePending();
      currentClipIdRef.current = clipId;
      awaitingLoadRef.current = true;
      lastSavedRef.current = "";
    }
  }, [clipId, firePending]);

  useEffect(() => {
    if (!enabled || !clipId) return;
    // Stale window: this coverImage belongs to another clip.
    if (loadedClipId !== clipId) return;

    // `null` is a real value (removal) and is diffed/saved like any other —
    // no early return on it, unlike keyframes/transitions.
    const serialized = serializeCoverImage(coverImage);
    if (awaitingLoadRef.current) {
      awaitingLoadRef.current = false;
      lastSavedRef.current = serialized;
      return;
    }
    if (serialized === lastSavedRef.current) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    pendingRef.current = { clipId, coverImage, serialized };
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending) void performSave(pending);
    }, delay);
    // Clears only the timer; pendingRef survives for the unmount flush.
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [coverImage, loadedClipId, clipId, enabled, delay, performSave]);

  // Unmount: flush instead of dropping.
  useEffect(() => () => firePendingRef.current(), []);

  return { saving };
}
